import { Suspense, lazy, ComponentType } from "react";
import { Loader2 } from "lucide-react";

interface LoadingFallbackProps {
  message?: string;
}

/**
 * Default loading fallback component
 */
export function LoadingFallback({ message = "Loading..." }: LoadingFallbackProps) {
  return (
    <div className="flex items-center justify-center min-h-[200px] p-8">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-coral mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}

/**
 * Wrapper to lazily load a component with a loading fallback
 */
export function withLazyLoad<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  fallback?: React.ReactNode
) {
  const LazyComponent = lazy(importFn);

  return function LazyWrapper(props: React.ComponentProps<T>) {
    return (
      <Suspense fallback={fallback || <LoadingFallback />}>
        <LazyComponent {...props} />
      </Suspense>
    );
  };
}

// Lazy-loaded heavy components
export const LazyStoryThemeAndIdeas = lazy(() => import("@/components/StoryThemeAndIdeas"));
export const LazyStyleSelection = lazy(() => import("@/components/StyleSelection"));
export const LazyTitleSelection = lazy(() => import("@/components/TitleSelection"));
export const LazyPhotoUpload = lazy(() => import("@/components/PhotoUpload"));
